"use client";

import { html } from "@codemirror/lang-html";
import { EditorContent } from "@tiptap/react";
import CodeMirror from "@uiw/react-codemirror";
import beautify from "js-beautify";
import React, { useCallback, useEffect, useRef, useState, type ChangeEvent } from "react";

import { uploadFile } from "@/api/admin/uploadFile";
import { toast } from "@/hooks/useToast";

// Hooks
import {
	sanitizeHTML,
	useEditorSetup,
	useLinkHandlers,
	useResponsiveStyles,
	useTableSettingsState,
	useTableStyleAppliers,
} from "./hooks";

// Components
import { ImageToolbar, MainToolbar, TableToolbar } from "./components";

// Modals
import type { UnifiedResponsiveStyles } from "./modals";
import {
	createEmptyResponsiveStyles,
	HorizontalRuleModal,
	ImageLinkModal,
	LinkModal,
	ResponsiveStylesModal,
	TableCreationModal,
	UnifiedTableSettingsModal,
} from "./modals";

// Types
import type {
	RichTextEditorProps,
	SelectionState,
} from "./types";

// Utils
import { normalizeTablesInHTML } from "./utils/tableSanitizer";
import { getCurrentColumnIndex, getCurrentRowIndex } from "./utils/tableUtils";

const RichTextEditor: React.FC<RichTextEditorProps> = ({
	value = "",
	onChange,
	placeholder = "Start writing...",
}) => {
	const [showHtml, setShowHtml] = useState(false);
	const [isDarkMode, setIsDarkMode] = useState(false);
	const [htmlContent, setHtmlContent] = useState(value);
	const fileInputRef = useRef<HTMLInputElement>(null);

	// Selection state
	const [selectionState, setSelectionState] = useState<SelectionState>({
		isInTable: false,
		isImageSelected: false,
		currentImageLink: null,
		canMergeCells: false,
		canSplitCell: false,
		isInHeaderCell: false,
		currentColumnIndex: null,
	});

	// Unified responsive styles state
	const [showResponsiveStyles, setShowResponsiveStyles] = useState(false);
	const [responsiveStyles, setResponsiveStyles] = useState<UnifiedResponsiveStyles>(
		createEmptyResponsiveStyles()
	);
	/** Range at the moment "Text Styles" was opened (modal focus collapses editor selection otherwise). */
	const responsiveStylesSelectionRef = useRef<{ from: number; to: number } | null>(null);

	// Horizontal Rule modal state
	const [showHorizontalRuleModal, setShowHorizontalRuleModal] = useState(false);

	// Table settings state (using custom hook)
	const tableState = useTableSettingsState();

	// Editor setup
	const editor = useEditorSetup({
		value,
		placeholder,
		onChange,
		onSelectionStateChange: setSelectionState,
		isInternalChangeRef: tableState.isInternalChange,
	});

	// Link handlers
	const linkHandlers = useLinkHandlers({
		editor,
		onSelectionStateChange: setSelectionState,
	});

	// Responsive styles
	const { applyUnifiedResponsiveStyles } = useResponsiveStyles({ editor, onChange });

	// Table style appliers (using custom hook)
	const tableStyleAppliers = useTableStyleAppliers({
		editor,
		onChange,
		isInternalChange: tableState.isInternalChange,
		tableSettings: tableState.tableSettings,
		columnSettings: tableState.columnSettings,
		rowSettings: tableState.rowSettings,
		cellSettings: tableState.cellSettings,
		allColumnSettings: tableState.allColumnSettings,
		setAllColumnSettings: tableState.setAllColumnSettings,
		allRowSettings: tableState.allRowSettings,
		setAllRowSettings: tableState.setAllRowSettings,
		currentTableId: tableState.currentTableId,
		selectedCellIndices: tableState.selectedCellIndices,
	});

	const [mounted, setMounted] = useState(false);
	useEffect(() => setMounted(true), []);

	// Get current column index helper
	const getColumnIndex = useCallback(() => {
		return getCurrentColumnIndex(editor);
	}, [editor]);

	// Insert table handler
	const handleInsertTable = useCallback(() => {
		tableStyleAppliers.insertTable(
			tableState.tableRows,
			tableState.tableCols,
			tableState.tableWithHeader,
			tableState.tableBorderColor,
			tableState.tableBorderWidth
		);
		tableState.setShowTableModal(false);
		tableState.setTableRows(3);
		tableState.setTableCols(3);
		tableState.setTableWithHeader(true);
		tableState.setTableBorderColor("#000000");
		tableState.setTableBorderWidth(1);
	}, [editor, tableStyleAppliers, tableState]);

	// Open unified table settings
	const handleOpenUnifiedTableSettings = useCallback(() => {
		tableState.openUnifiedTableSettings(editor);
	}, [editor, tableState]);

	// Delete table handler (regenerates CSS for remaining tables)
	const handleDeleteTable = useCallback(() => {
		tableStyleAppliers.deleteTable();
	}, [tableStyleAppliers]);

	// Sync content
	useEffect(() => {
		if (tableState.isInternalChange.current) return;
		if (!showHtml && editor) {
			const currentHtml = editor.getHTML();
			if (value !== currentHtml) {
				// Repair tables in the incoming value too, so content loaded from
				// elsewhere lands in the editor already normalised.
				const cleanValue = normalizeTablesInHTML(
					sanitizeHTML(value.replace(/<li>\s*<p>(.*?)<\/p>\s*<\/li>/g, "<li>$1</li>")),
				);
				editor.commands.setContent(cleanValue, { emitUpdate: false });
			}
		} else if (showHtml && value) {
			const formatHtml = (htmlStr: string) => {
				const styleTagRegex = /<style>([\s\S]*?)<\/style>/g;
				const htmlWithBeautifiedCss = htmlStr.replace(styleTagRegex, (_, cssContent) => {
					const formattedCss = beautify.css(cssContent, {
						indent_size: 2,
						indent_char: " ",
						end_with_newline: false,
						preserve_newlines: false,
						newline_between_rules: true,
						space_around_combinator: true,
					});
					return `<style>\n${formattedCss}\n</style>`;
				});
				return beautify.html(htmlWithBeautifiedCss, {
					indent_size: 2,
					preserve_newlines: false,
					max_preserve_newlines: 0,
					wrap_line_length: 0,
					unformatted: [],
					content_unformatted: ["style", "script"],
					indent_inner_html: true,
					extra_liners: [],
					end_with_newline: true,
					inline: [],
				});
			};
			setHtmlContent(formatHtml(value));
		}
	}, [value, editor, showHtml, tableState.isInternalChange]);

	// Track cell selection changes and reopen modal when cell changes
	useEffect(() => {
		if (!editor || !tableState.showUnifiedTableSettings) return;

		// Create a unique identifier for the current cell (includes table ID to detect table switches)
		const getCellIdentifier = () => {
			if (!editor.isActive("table")) return null;
			const rowIndex = getCurrentRowIndex(editor);
			const colIndex = getCurrentColumnIndex(editor);
			const tableId = editor.getAttributes("table")["data-table-id"] || "";
			return `${tableId}-${rowIndex}-${colIndex}`;
		};

		let lastCellId = getCellIdentifier();

		// Listen to selection updates
		const handleSelectionUpdate = () => {
			// FIRST: Check if we should prevent auto-reopen (before any other logic)
			if (tableState.shouldPreventAutoReopen.current) {
				console.log("Auto-reopen prevented by flag");
				return;
			}

			const currentCellId = getCellIdentifier();

			// If cell changed (and we're still in a table), close and reopen modal
			if (currentCellId && lastCellId && currentCellId !== lastCellId) {
				console.log("Cell changed, reopening modal. From:", lastCellId, "To:", currentCellId);

				// Close modal
				tableState.setShowUnifiedTableSettings(false);

				// Small delay to ensure clean close/open
				setTimeout(() => {
					// Reopen with new cell data
					tableState.openUnifiedTableSettings(editor);
				}, 50);
			}

			lastCellId = currentCellId;
		};

		// Use ProseMirror's editor event
		editor.on("selectionUpdate", handleSelectionUpdate);

		return () => {
			editor.off("selectionUpdate", handleSelectionUpdate);
		};
	}, [editor, tableState.showUnifiedTableSettings, tableState]);

	const toggleView = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		setShowHtml((prev) => !prev);
	}, []);

	const handleHtmlChange = (val: string) => {
		setHtmlContent(val);
		const cleanedValue = val.replace(/<style>[\s\S]*?<\/style>/gi, "");
		onChange?.(cleanedValue);
	};

	const handleInsertHorizontalRule = useCallback((color: string, width: string, thickness: string) => {
		if (!editor) return;
		editor.chain().focus().insertContent({
			type: "horizontalRule",
			attrs: { color, width, thickness },
		}).run();
	}, [editor]);

	const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
		event.stopPropagation();
		const file = event.target.files?.[0];
		if (!file || !editor) return;
		const fileUrl = await uploadFile(file, toast);
		if (fileUrl) {
			// Check if the file is a PDF
			if (file.type === "application/pdf") {
				// Insert PDF as a link
				const fileName = file.name || "Download PDF";
				editor
					.chain()
					.focus()
					.insertContent({
						type: "text",
						text: fileName,
						marks: [
							{
								type: "link",
								attrs: {
									href: fileUrl,
									target: "_blank",
								},
							},
						],
					})
					.run();
			} else {
				// Insert as image (for gif, webp)
				editor.chain().focus().setImage({ src: fileUrl }).run();
			}
		}
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	const triggerFileInput = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		fileInputRef.current?.click();
	};

	const handleEditorClick = useCallback(() => {
		if (!editor) return;
		setTimeout(() => {
			const inTable = editor.isActive("table");
			const imageActive = editor.isActive("image");
			const imageLink = imageActive ? editor.getAttributes("image").link || null : null;
			const canMerge = editor.can().mergeCells();
			const canSplit = editor.can().splitCell();
			const isHeader = editor.isActive("tableHeader");

			let colIndex: number | null = null;
			if (inTable) {
				colIndex = getColumnIndex();
			}

			setSelectionState((prev) => {
				if (
					prev.isInTable === inTable &&
					prev.isImageSelected === imageActive &&
					prev.currentImageLink === imageLink &&
					prev.canMergeCells === canMerge &&
					prev.canSplitCell === canSplit &&
					prev.isInHeaderCell === isHeader &&
					prev.currentColumnIndex === colIndex
				) {
					return prev;
				}
				return {
					isInTable: inTable,
					isImageSelected: imageActive,
					currentImageLink: imageLink,
					canMergeCells: canMerge,
					canSplitCell: canSplit,
					isInHeaderCell: isHeader,
					currentColumnIndex: colIndex,
				};
			});
		}, 0);
	}, [editor, getColumnIndex]);

	useEffect(() => {
		editor?.on("selectionUpdate", ({ editor }) => {
			const selectionType =
				editor.isActive("heading")
					? "heading"
					: editor.isActive("bulletList")
						? "bulletList"
						: editor.isActive("orderedList")
							? "orderedList"
							: "paragraph";

			setSelectionState(prev => ({
				...prev,
				selectionType: selectionType as SelectionState["selectionType"],
			}))
		});
	}, [editor]);

	return (
		<div
			onClick={(e) => e.stopPropagation()}
			className="border border-gray-300 rounded-lg overflow-hidden bg-white shadow-sm flex flex-col h-full min-h-0 min-w-0 w-full"
		>
			<MainToolbar
				editor={editor}
				showHtml={showHtml}
				isDarkMode={isDarkMode}
				onToggleView={toggleView}
				onToggleTheme={(e) => {
					e.stopPropagation();
					setIsDarkMode((prev) => !prev);
				}}
				onSetLink={linkHandlers.setLink}
				onTriggerFileInput={triggerFileInput}
				onColorChange={(color) => editor?.chain().focus().setColor(color).run()}
				onHighlightChange={(color) => editor?.chain().focus().setHighlight({ color }).run()}
				onClearColor={() => editor?.chain().focus().setMark("textStyle", { color: null }).run()}
				onClearHighlight={() => editor?.chain().focus().unsetHighlight().run()}
				onShowTableModal={() => tableState.setShowTableModal(true)}
				onShowHorizontalRuleModal={() => setShowHorizontalRuleModal(true)}
			/>

			{/* <FontControlsToolbar
				editor={editor}
				onShowResponsiveStylesModal={(styles, selection) => {
					responsiveStylesSelectionRef.current = selection;
					setResponsiveStyles(styles);
					setShowResponsiveStyles(true);
				}}
			/> */}

			<TableToolbar
				editor={editor}
				selectionState={selectionState}
				onSelectionStateChange={(state) => setSelectionState((prev) => ({ ...prev, ...state }))}
				onOpenUnifiedTableSettings={handleOpenUnifiedTableSettings}
				onDeleteTable={handleDeleteTable}
			/>

			<ImageToolbar
				selectionState={selectionState}
				onOpenImageLinkModal={linkHandlers.openImageLinkModal}
				onRemoveImageLink={linkHandlers.removeImageLink}
			/>

			{/* Modals */}
			<LinkModal
				isOpen={linkHandlers.showLinkModal}
				linkUrl={linkHandlers.linkUrl}
				linkText={linkHandlers.linkText}
				onLinkUrlChange={linkHandlers.setLinkUrl}
				onLinkTextChange={linkHandlers.setLinkText}
				onSubmit={linkHandlers.handleLinkSubmit}
				onClose={linkHandlers.handleLinkClose}
			/>

			<ImageLinkModal
				isOpen={linkHandlers.showImageLinkModal}
				imageLinkUrl={linkHandlers.imageLinkUrl}
				imageLinkTarget={linkHandlers.imageLinkTarget}
				hasExistingLink={!!editor?.getAttributes("image").link}
				onImageLinkUrlChange={linkHandlers.setImageLinkUrl}
				onImageLinkTargetChange={linkHandlers.setImageLinkTarget}
				onSubmit={linkHandlers.handleImageLinkSubmit}
				onRemove={linkHandlers.removeImageLink}
				onClose={linkHandlers.handleImageLinkClose}
			/>

			<HorizontalRuleModal
				isOpen={showHorizontalRuleModal}
				onClose={() => setShowHorizontalRuleModal(false)}
				onInsert={handleInsertHorizontalRule}
			/>

			<ResponsiveStylesModal
				isOpen={showResponsiveStyles}
				responsiveStyles={responsiveStyles}
				onStylesChange={setResponsiveStyles}
				onApply={() =>
					applyUnifiedResponsiveStyles(
						responsiveStyles,
						() => {
							responsiveStylesSelectionRef.current = null;
							setShowResponsiveStyles(false);
							setResponsiveStyles(createEmptyResponsiveStyles());
						},
						responsiveStylesSelectionRef.current
					)
				}
				onClose={() => {
					responsiveStylesSelectionRef.current = null;
					setShowResponsiveStyles(false);
					setResponsiveStyles(createEmptyResponsiveStyles());
				}}
			/>

			<TableCreationModal
				isOpen={tableState.showTableModal}
				tableRows={tableState.tableRows}
				tableCols={tableState.tableCols}
				tableWithHeader={tableState.tableWithHeader}
				onRowsChange={tableState.setTableRows}
				onColsChange={tableState.setTableCols}
				onWithHeaderChange={tableState.setTableWithHeader}
				onInsert={handleInsertTable}
				onClose={() => {
					tableState.setShowTableModal(false);
					tableState.setTableRows(3);
					tableState.setTableCols(3);
					tableState.setTableWithHeader(true);
				}}
			/>

			<UnifiedTableSettingsModal
				isOpen={tableState.showUnifiedTableSettings}
				tableSettings={tableState.tableSettings}
				onTableSettingsChange={(settings) => tableState.setTableSettings((prev) => ({ ...prev, ...settings }))}
				columnSettings={tableState.columnSettings}
				onColumnSettingsChange={(settings) => tableState.setColumnSettings((prev) => ({ ...prev, ...settings }))}
				onColumnSelect={tableState.handleColumnSelect}
				totalColumns={tableState.totalColumns}
				rowSettings={tableState.rowSettings}
				onRowSettingsChange={(settings) => tableState.setRowSettings((prev) => ({ ...prev, ...settings }))}
				onRowSelect={tableState.handleRowSelect}
				totalRows={tableState.totalRows}
				cellSettings={tableState.cellSettings}
				onCellSettingsChange={(settings) => tableState.setCellSettings((prev) => ({ ...prev, ...settings }))}
				onApplyTableSettings={tableStyleAppliers.applyTableSettings}
				onApplyColumnSettings={tableStyleAppliers.applyColumnStyles}
				onApplyRowSettings={tableStyleAppliers.applyRowStyles}
				onApplyCellSettings={tableStyleAppliers.applyCellStyles}
				onResetColumnWidths={tableStyleAppliers.resetColumnWidths}
				onClose={() => tableState.setShowUnifiedTableSettings(false)}
				onCloseWithoutReopen={tableState.closeUnifiedTableSettingsWithoutReopen}
			/>

			{/* Content */}
			<div
				className={`flex-1 min-h-0 min-w-0 w-full p-2 ${isDarkMode ? "bg-gray-900" : "bg-white"} overflow-auto`}
				onClick={(e) => e.stopPropagation()}
			>
				{!mounted ? null : showHtml ? (
					<div className="h-full min-h-0 min-w-0 w-full overflow-auto [&_.cm-editor]:min-w-0 [&_.cm-editor]:max-w-full">
						<CodeMirror
							value={htmlContent}
							height="100%"
							className="min-w-0"
							extensions={[html()]}
							onChange={(val) => handleHtmlChange(val)}
							basicSetup={{
								lineNumbers: true,
								foldGutter: true,
								highlightActiveLine: true,
							}}
						/>
					</div>
				) : editor ? (
					<div
						className="h-full min-w-0 w-full prose prose-sm max-w-full [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[300px] [&_.ProseMirror]:max-h-full [&_.ProseMirror]:min-w-0 [&_.ProseMirror]:max-w-full [&_.ProseMirror]:break-words [&_.ProseMirror_pre]:max-w-full [&_.ProseMirror_pre]:overflow-x-auto [&_.ProseMirror_blockquote]:border-l-4 [&_.ProseMirror_blockquote]:border-blue-500 [&_.ProseMirror_blockquote]:pl-4 [&_.ProseMirror_blockquote]:py-2 [&_.ProseMirror_blockquote]:my-4 [&_.ProseMirror_blockquote]:italic [&_.ProseMirror_blockquote]:text-gray-700 [&_.ProseMirror_blockquote]:bg-gray-50 [&_.ProseMirror_table]:border-separate [&_.ProseMirror_table:not([data-border-spacing])]:border-spacing-0 [&_.ProseMirror_table]:w-full [&_.ProseMirror_table]:my-4 [&_.ProseMirror_table]:select-none [&_.ProseMirror_td]:p-2 [&_.ProseMirror_td]:min-w-[100px] [&_.ProseMirror_td]:cursor-cell [&_.ProseMirror_td]:transition-all [&_.ProseMirror_td:hover]:ring-2 [&_.ProseMirror_td:hover]:ring-blue-300 [&_.ProseMirror_td:hover]:ring-inset [&_.ProseMirror_th]:p-2 [&_.ProseMirror_th]:font-semibold [&_.ProseMirror_th]:min-w-[100px] [&_.ProseMirror_th]:cursor-cell [&_.ProseMirror_th]:transition-all [&_.ProseMirror_th:hover]:ring-2 [&_.ProseMirror_th:hover]:ring-blue-300 [&_.ProseMirror_th:hover]:ring-inset [&_.ProseMirror_.selectedCell]:bg-blue-200 [&_.ProseMirror_.selectedCell]:outline [&_.ProseMirror_.selectedCell]:outline-[3px] [&_.ProseMirror_.selectedCell]:outline-blue-600 [&_.ProseMirror_.selectedCell]:outline-offset-[-3px] [&_.ProseMirror_.selectedCell]:relative [&_.ProseMirror_.selectedCell]:z-10 [&_.ProseMirror_.selectedCell:hover]:ring-0 [&_.ProseMirror_.tableWrapper]:overflow-x-auto [&_.ProseMirror_.tableWrapper]:my-4 [&_.ProseMirror_.resize-cursor]:cursor-col-resize"
						onClick={handleEditorClick}
					>
						<EditorContent
							editor={editor}
						/>
					</div>
				) : null}
			</div>

			<input
				type="file"
				accept="image/gif,image/webp,application/pdf,video/mp4"
				ref={fileInputRef}
				onChange={handleFileChange}
				style={{ display: "none" }}
			/>
		</div>
	);
};

export default RichTextEditor;
